import { Component, OnInit } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { iif, of, Subject } from 'rxjs';
import { concatMap, map, takeUntil, tap } from 'rxjs/operators';
import { ResponseModel } from 'src/app/modules/shared/models/reponse.model';
import { ProgressData } from '../../../../shared/dtos/progress-data';
import { ProgressIndicatingService } from '../../../../shared/services/progress-indicating.service';
import { RoleDto } from '../../dtos/role.dto';
import { InternalUserModel } from '../../models/internal-user.model';
import { InternalUsersService } from '../../services/internal-users.service';
import { UpdateUserDialogComponent } from '../update-user-dialog/update-user-dialog.component';

@Component({
    selector: 'app-internal-users',
    templateUrl: './internal-users.component.html',
    styleUrls: ['./internal-users.component.scss']
})
export class InternalUsersComponent implements OnInit {
    public users: InternalUserModel[] = [];
    private progress$ = new Subject<ProgressData>();
    private unsubscribe$ = new Subject<void>();
    private roles: RoleDto[] = [];

    public constructor(
        private messageService: MessageService,
        private dialogService: DialogService,
        private confirmationService: ConfirmationService,
        private progressIndicatingService: ProgressIndicatingService,
        private internalUserService: InternalUsersService) {

    }

    public ngOnInit(): void {
        this.loadUsers();
    }

    private loadUsers(): void {
        this.progressIndicatingService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Loading Users Details',
                details: 'Please wait...',
                done: false
            }
        });
        this.internalUserService
            .getUsers()
            .pipe(
                map(res => res.userCUModel.map(r => new InternalUserModel(r))),
                tap(users => {
                    this.users = users;
                }),
                concatMap(() => {
                    return this.internalUserService.getRoles();
                }),
                map(response => response.roleModel),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(
                (response: RoleDto[]) => {
                    this.sendProgressDoneSignal();
                    this.roles = response;
                },
                (error) => {
                    const response = new ResponseModel(error.error);
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'error', summary: 'Fail', detail: response.message });
                });
    }

    public onRowDeleted(user: InternalUserModel): void {
        this.confirmationService.confirm({
            message: 'Do you want to delete \'' + user.getFullName() + '\'?',
            header: 'Confirmation',
            icon: 'pi pi-info-circle',
            acceptLabel: 'Ok',
            acceptVisible: true,
            rejectLabel: 'Cancel',
            rejectVisible: true,
            accept: /* istanbul ignore next */ () => this.deleteUser(user.usersId)
        });
    }

    private deleteUser(userId: number): void {
        this.progressIndicatingService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Deleting User',
                details: 'Please wait...',
                done: false
            }
        });
        this.internalUserService
            .deleteUser(userId)
            .pipe(
                concatMap(() => {
                    return this.internalUserService.getUsers();
                }),
                map(res => res.userCUModel.map(r => new InternalUserModel(r))),
                tap(users => { this.users = users; }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(
                _ => {
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Successfully Deleted User.' });
                },
                (response) => {
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'error', summary: 'Fail', detail: response.error.error });
                });
    }

    public onRowEditClicked(user: InternalUserModel): void {
        const ref = this.dialogService.open(UpdateUserDialogComponent, {
            header: 'Edit User',
            closable: true,
            data: {
                roles: this.roles,
                user: user.getData()
            }
        });

        const loadUsersObs = this.internalUserService.getUsers().pipe(
            map(res => res.userCUModel.map(r => new InternalUserModel(r))),
            tap(users => { this.users = users; }),
            takeUntil(this.unsubscribe$)
        );

        ref.onClose
            .pipe(
                concatMap(
                    res => iif(() => res, loadUsersObs, of())
                ),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    public onAddNewUserClicked(): void {
        const ref = this.dialogService.open(UpdateUserDialogComponent, {
            header: 'Add New User',
            closable: true,
            data: {
                roles: this.roles
            }
        });

        const loadUsersObs = this.internalUserService.getUsers().pipe(
            map(res => res.userCUModel.map(r => new InternalUserModel(r))),
            tap(users => { this.users = users; }),
            takeUntil(this.unsubscribe$)
        );

        ref.onClose
            .pipe(
                concatMap(
                    res => iif(() => res, loadUsersObs, of())
                ),
                takeUntil(this.unsubscribe$)
            ).subscribe(res => {
                /* istanbul ignore else */
                if (res) {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Successfully Added New User.' });
                }
            });
    }

    public onStatusSwitchChanged(user: InternalUserModel): void {
        this.internalUserService
            .updateUsersStatus(user.usersId, user.status)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(
                response => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: response.responseMessage });
                },
                error => {
                    const response = new ResponseModel(error.error);
                    this.messageService.add({ severity: 'error', summary: 'Fail', detail: response.message });
                }
            );
    }

    public ngOnDestroy(): void {
        this.progress$.next({ header: '', done: true });
        this.progress$.complete();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private sendProgressDoneSignal(): void {
        this.progress$.next({
            header: '',
            done: true
        });
    }
}
